import React, { useState, useEffect } from 'react';
import { api } from './api';
import './AdminUsers.css';

export default function AdminSubcategories() {
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    section: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Фильтрация подкатегорий по поисковому запросу
    if (!searchQuery.trim()) {
      setFilteredSubcategories(subcategories);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = subcategories.filter(subcategory => {
        const title = (subcategory.title || '').toLowerCase();
        const slug = (subcategory.slug || '').toLowerCase();
        const sectionTitle = (subcategory.section_title || '').toLowerCase();
        return title.includes(query) || slug.includes(query) || sectionTitle.includes(query);
      });
      setFilteredSubcategories(filtered);
    }
  }, [searchQuery, subcategories]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subcategoriesData, sectionsData] = await Promise.all([
        api.getSubcategories(),
        api.getSections(),
      ]);
      // Если данные приходят в формате пагинации, извлекаем results
      const subcategories = Array.isArray(subcategoriesData) ? subcategoriesData : (subcategoriesData.results || subcategoriesData);
      const sections = Array.isArray(sectionsData) ? sectionsData : (sectionsData.results || sectionsData);
      setSubcategories(subcategories);
      setFilteredSubcategories(subcategories);
      setSections(sections);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const data = {
        ...formData,
        section: parseInt(formData.section),
      };
      
      if (editingSubcategory) {
        await api.updateSubcategory(editingSubcategory.id, data);
      } else {
        await api.createSubcategory(data);
      }
      setShowForm(false);
      setEditingSubcategory(null);
      setFormData({
        title: '',
        slug: '',
        section: '',
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      title: subcategory.title || '',
      slug: subcategory.slug || '',
      section: subcategory.section?.toString() || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту подкатегорию?')) {
      return;
    }

    try {
      await api.deleteSubcategory(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSubcategory(null);
    setFormData({
      title: '',
      slug: '',
      section: '',
    });
  };

  if (loading) {
    return <div className="admin-loading">Загрузка подкатегорий...</div>;
  }

  return (
    <div className="admin-users">
      <div className="admin-toolbar">
        <div className="admin-toolbar-actions">
          <div className="admin-search">
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Поиск подкатегорий..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="admin-search-clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button className="admin-add-button" onClick={() => setShowForm(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Добавить подкатегорию
        </button>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {showForm && (
        <div className="admin-form-overlay">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-header">
              <h4>{editingSubcategory ? 'Редактировать подкатегорию' : 'Создать подкатегорию'}</h4>
              <button type="button" className="form-close" onClick={handleCancel}>×</button>
            </div>

            <div className="form-group">
              <label>Название *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleTitleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Раздел *</label>
                <select name="section" value={formData.section} onChange={handleInputChange} required>
                  <option value="">Выберите раздел</option>
                  {sections.map(section => (
                    <option key={section.id} value={section.id}>{section.title}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>URL (slug) *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  pattern="[a-z0-9-]+"
                  title="Только строчные буквы, цифры и дефисы"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="admin-button admin-button-cancel" onClick={handleCancel}>
                Отмена
              </button>
              <button type="submit" className="admin-button admin-button-save">
                {editingSubcategory ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Раздел</th>
              <th>URL</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubcategories.length === 0 ? (
              <tr>
                <td colSpan="5" className="admin-empty">Нет подкатегорий</td>
              </tr>
            ) : (
              filteredSubcategories.map(subcategory => (
                <tr key={subcategory.id}>
                  <td>{subcategory.id}</td>
                  <td>{subcategory.title}</td>
                  <td>{subcategory.section_title || '-'}</td>
                  <td><code>{subcategory.slug}</code></td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-action-button admin-action-edit"
                        onClick={() => handleEdit(subcategory)}
                        title="Редактировать"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="admin-action-button admin-action-delete"
                        onClick={() => handleDelete(subcategory.id)}
                        title="Удалить"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}







